<script setup lang="ts">
  import { computed } from "vue";
  import { storeToRefs } from "pinia";

  import { PickerDialog, type PickerGroup, type PickerItem } from "@/components/episto-ui/picker";
  import CardPreview from "./CardTypePicker/CardPreview.vue";

  import { useCardTypeAccess } from "@utils/composables/useCardTypeAccess";
  import { useRecodeTypeItems } from "@/composables/useRecodeTypeItems";
  import { useConstantsStore } from "@stores/constants";
  import { useQuestionCardTypes } from "@utils/composables/questionCard";
  import { cardTypeName } from "@utils/constants/cardTypes";
  import { CARD_CATALOG, CARD_GROUPS, CARD_SECTIONS, type CardGroup, type CardPreviewSpec } from "@utils/constants/cardCatalog";
  import { recodeTypeKey } from "@utils/recodeKinds";

  withDefaults(
    defineProps<{
      title: string;
      description: string;
      actionLabel?: string;
      initialKey?: string;
    }>(),
    { actionLabel: "Insert card", initialKey: undefined },
  );

  const emit = defineEmits<{ select: [key: string] }>();

  const open = defineModel<boolean>("open", { default: false });

  const { isCardTypeDisabled, cardTypeDisabledReason } = useCardTypeAccess();
  const { cardSubTypesInfos, cardTypesGroups } = storeToRefs(useConstantsStore());
  const { determineIcon } = useQuestionCardTypes(cardSubTypesInfos);
  const { availableRecodeItems } = useRecodeTypeItems();

  const groups: PickerGroup[] = CARD_GROUPS;

  const catalogOrder = Object.keys(CARD_CATALOG);
  const byCatalogOrder = (a: PickerItem, b: PickerItem) => {
    const indexA = catalogOrder.indexOf(a.key);
    const indexB = catalogOrder.indexOf(b.key);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  };

  const toItem = (key: string, group: CardGroup): PickerItem => {
    const entry = CARD_CATALOG[key];
    const locked = isCardTypeDisabled(key);
    return {
      key,
      group,
      section: entry?.section ?? "Other",
      name: cardTypeName(key, cardSubTypesInfos.value[key]?.name),
      description: entry?.description ?? "",
      about: entry?.about,
      icon: determineIcon(key),
      locked,
      lockedReason: cardTypeDisabledReason(key) ?? (locked ? "You can't add this card type to this survey." : null),
    };
  };

  const recodeItems = computed<PickerItem[]>(() =>
    availableRecodeItems.value
      .filter((item) => !item.analysisOnly)
      .map((item) => ({
        key: recodeTypeKey(item.key),
        group: "recodes",
        section: "Recodes",
        name: item.name,
        description: item.description,
        about: item.about ?? CARD_CATALOG.q_hidden.about,
        icon: determineIcon("q_hidden"),
      })),
  );

  const items = computed<PickerItem[]>(() => [
    ...(cardTypesGroups.value as [CardGroup, string[]][])
      .flatMap(([group, subTypes]) =>
        subTypes.filter((key) => key !== "q_hidden" && cardSubTypesInfos.value[key]).map((key) => toItem(key, group)),
      )
      .sort(byCatalogOrder),
    ...recodeItems.value,
  ]);

  const previewFor = (item: PickerItem): CardPreviewSpec => {
    if (item.group !== "recodes") return CARD_CATALOG[item.key]?.preview ?? { kind: "message", question: item.name };
    const example = availableRecodeItems.value.find((recode) => recodeTypeKey(recode.key) === item.key)?.example;
    return example ? { kind: "hidden", example } : CARD_CATALOG.q_hidden.preview;
  };
</script>

<template>
  <PickerDialog
    v-model:open="open"
    :title="title"
    :description="description"
    :groups="groups"
    :items="items"
    :sections="CARD_SECTIONS"
    :action-label="actionLabel"
    :initial-key="initialKey"
    footnote="Cards you add here can be reordered and duplicated later in the flow."
    @select="emit('select', $event as string)">
    <template #preview="{ item }">
      <CardPreview :preview="previewFor(item)" />
    </template>
  </PickerDialog>
</template>
